import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { SignOut, Plus, Trash, Package, MapPin, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatINR } from "@/lib/api";
import { RevealLines } from "@/components/Reveal";

const EMPTY_ADDR = { label: "Home", line1: "", line2: "", city: "", state: "", pincode: "", phone: "" };

const Account = () => {
  const nav = useNavigate();
  const { user, ready, logout } = useAuth();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [showAddr, setShowAddr] = useState(false);
  const [addr, setAddr] = useState(EMPTY_ADDR);

  useEffect(() => {
    if (ready && !user) nav("/account/login");
  }, [ready, user, nav]);

  useEffect(() => {
    if (!user) return;
    api.get("/customer/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/customer/addresses").then((r) => setAddresses(r.data)).catch(() => {});
  }, [user]);

  const addAddress = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/customer/addresses", addr);
      setAddresses(data);
      setShowAddr(false);
      setAddr(EMPTY_ADDR);
      toast.success("Address saved");
    } catch {
      toast.error("Could not save address");
    }
  };

  const removeAddr = async (id) => {
    const { data } = await api.delete(`/customer/addresses/${id}`);
    setAddresses(data);
    toast.success("Address removed");
  };

  const doLogout = () => {
    logout();
    nav("/");
  };

  if (!user) return <div className="pt-[72px] min-h-screen" />;

  return (
    <div className="pt-[72px] min-h-screen">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="overline mb-3">My Account</p>
            <RevealLines tag="h1" immediate lines={[`Hello, ${user.name?.split(" ")[0] || "there"}`]} className="font-serif text-5xl md:text-7xl text-bone leading-[0.85]" />
            <p className="font-mono text-xs text-ash mt-4">{user.email}</p>
          </div>
          <button onClick={doLogout} data-testid="account-logout" className="overline flex items-center gap-2 text-ash hover:text-crimson">
            Sign Out <SignOut size={16} />
          </button>
        </div>

        <div className="flex gap-1 mb-10 border-b border-line">
          {[["orders", "Orders", Package], ["addresses", "Addresses", MapPin]].map(([k, label, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`account-tab-${k}`}
              className={`flex items-center gap-2 px-6 py-3 font-mono text-xs uppercase tracking-widest border-b-2 -mb-px transition-colors ${tab === k ? "border-crimson text-bone" : "border-transparent text-ash hover:text-bone"}`}>
              <Icon size={16} /> {label}
            </button>
          ))}
        </div>

        {tab === "orders" && (
          <div>
            {orders.length === 0 ? (
              <div className="py-16">
                <p className="font-serif text-3xl text-ash italic">No orders yet.</p>
                <Link to="/shop" className="btn-fill inline-flex items-center gap-3 px-8 py-3 overline text-xs mt-6">Start Shopping</Link>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((o) => (
                  <div key={o.id} data-testid={`account-order-${o.id}`} className="border border-line p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <p className="text-bone">{(o.items || []).map((i) => `${i.name} × ${i.qty}`).join(", ")}</p>
                      <p className="font-mono text-xs text-ash mt-2">{new Date(o.created_at).toLocaleString()}</p>
                    </div>
                    <div className="flex items-center gap-6">
                      <span className={`font-mono text-[10px] uppercase px-2 py-1 border ${o.payment_status === "paid" ? "text-gold border-gold" : "text-ash border-line"}`}>{o.payment_status}</span>
                      <span className="font-serif text-2xl text-bone">{formatINR(o.amount)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "addresses" && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {addresses.map((a) => (
                <div key={a.id} data-testid={`address-${a.id}`} className="border border-line p-6 relative">
                  <button onClick={() => removeAddr(a.id)} className="absolute top-4 right-4 text-ash hover:text-crimson" data-testid={`remove-address-${a.id}`}><Trash size={16} /></button>
                  <p className="overline mb-3">{a.label}</p>
                  <p className="text-bone text-sm leading-relaxed">{a.line1}{a.line2 ? `, ${a.line2}` : ""}<br />{a.city}{a.state ? `, ${a.state}` : ""} — {a.pincode}</p>
                  {a.phone && <p className="font-mono text-xs text-ash mt-3">{a.phone}</p>}
                </div>
              ))}
              <button onClick={() => setShowAddr(true)} data-testid="add-address-button" className="border border-dashed border-line p-6 flex flex-col items-center justify-center gap-2 text-ash hover:text-bone hover:border-bone transition-colors min-h-[140px]">
                <Plus size={22} weight="thin" />
                <span className="overline">Add Address</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {showAddr && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-[#141416] border border-line w-full max-w-lg my-8">
            <div className="flex items-center justify-between p-6 border-b border-line">
              <p className="overline">New Address</p>
              <button onClick={() => setShowAddr(false)} className="text-ash hover:text-bone"><X size={20} /></button>
            </div>
            <form onSubmit={addAddress} className="p-6 space-y-4">
              <input placeholder="Label (Home / Work)" value={addr.label} onChange={(e) => setAddr({ ...addr, label: e.target.value })} data-testid="addr-label" className="addr-inp" />
              <input placeholder="Address line 1" required value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} data-testid="addr-line1" className="addr-inp" />
              <input placeholder="Address line 2 (optional)" value={addr.line2} onChange={(e) => setAddr({ ...addr, line2: e.target.value })} data-testid="addr-line2" className="addr-inp" />
              <div className="grid grid-cols-2 gap-4">
                <input placeholder="City" required value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} data-testid="addr-city" className="addr-inp" />
                <input placeholder="State" value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })} data-testid="addr-state" className="addr-inp" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <input placeholder="Pincode" required value={addr.pincode} onChange={(e) => setAddr({ ...addr, pincode: e.target.value })} data-testid="addr-pincode" className="addr-inp" />
                <input placeholder="Phone" value={addr.phone} onChange={(e) => setAddr({ ...addr, phone: e.target.value })} data-testid="addr-phone" className="addr-inp" />
              </div>
              <button type="submit" data-testid="save-address" className="btn-fill w-full py-4 overline text-sm">Save Address</button>
            </form>
          </div>
        </div>
      )}

      <style>{`.addr-inp{width:100%;background:transparent;border:1px solid var(--border);padding:0.65rem 0.85rem;color:var(--text-primary);font-family:'Space Mono',monospace;font-size:0.8rem;outline:none}.addr-inp:focus{border-color:var(--crimson)}.addr-inp::placeholder{color:#52525b}`}</style>
    </div>
  );
};

export default Account;
